import React, { useState, useEffect, useCallback, useRef } from 'react';
import ChatWindow from '../components/ChatWindow';
import Sidebar from '../components/Sidebar';
import AppHeader from '../components/AppHeader';
import { chatStorage, Chat, Message, createEphemeralChat } from '../utils/chatStorage';
import { listMyChats } from '../api/chats';
import { useAuth } from '../auth/useAuth';
import { track } from '../utils/analytics';

export default function ChatApp() {
  const { status } = useAuth();
  const isSignedIn = status === 'authenticated';

  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 769);
  const [escalatedChats, setEscalatedChats] = useState<Record<string, { ticketId: string; email: string }>>({});
  const [raiseTicketChatId, setRaiseTicketChatId] = useState<string | null>(null);
  const loadedForStatusRef = useRef<string | null>(null);

  // Anonymous chats live in this browser's localStorage; signed-in chats are
  // fetched from the server, keyed by account_id — the two are never merged.
  // Re-run whenever auth status settles to a *new* value (sign-in, sign-out,
  // or a session lapse). 'loading' (the initial /api/auth/me check) is
  // skipped so a signed-in user never sees a flash of anonymous history
  // before that resolves.
  useEffect(() => {
    if (status === 'loading') return;
    if (loadedForStatusRef.current === status) return;
    loadedForStatusRef.current = status;

    if (status === 'authenticated') {
      setChatsLoading(true);
      listMyChats()
        .then((serverChats) => {
          if (serverChats.length > 0) {
            setChats(serverChats);
            setCurrentChatId(serverChats[0].id);
          } else {
            const fresh = createEphemeralChat();
            setChats([fresh]);
            setCurrentChatId(fresh.id);
          }
        })
        .catch(() => {
          // Can't reach chat history right now — a fresh, in-memory chat is
          // still usable; it isn't stale/wrong data, just not history.
          const fresh = createEphemeralChat();
          setChats([fresh]);
          setCurrentChatId(fresh.id);
        })
        .finally(() => setChatsLoading(false));
      return;
    }

    // unauthenticated (or error) — anonymous local history
    const loadedChats = chatStorage.getAllChats();
    if (loadedChats.length === 0) {
      const newChat = chatStorage.createChat();
      setChats([newChat]);
      setCurrentChatId(newChat.id);
    } else {
      setChats(loadedChats);
      setCurrentChatId(loadedChats[0].id);
    }
    setChatsLoading(false);
  }, [status]);

  const handleNewChat = (initialModule?: string) => {
    track('new_chat');
    // Signed-in: in-memory only — there's no "create chat" endpoint; the
    // server vivifies the document on the first /api/chat call for this id,
    // same as the anonymous flow below.
    const newChat = isSignedIn ? createEphemeralChat() : chatStorage.createChat();
    if (initialModule !== undefined) {
      if (!isSignedIn) chatStorage.updateChat(newChat.id, { selectedModule: initialModule });
      newChat.selectedModule = initialModule;
    }
    setChats(prev => [newChat, ...prev]);
    setCurrentChatId(newChat.id);
  };

  const handleSelectChat = (chatId: string) => {
    setCurrentChatId(chatId);
    if (window.innerWidth < 769) setSidebarOpen(false);
  };

  const handleDeleteChat = (chatId: string) => {
    // No delete endpoint for server-backed chats (see routes/chats.py) —
    // Sidebar hides this action entirely when signed in, but stay safe here
    // regardless of how it's invoked.
    if (!isSignedIn) chatStorage.deleteChat(chatId);
    const updatedChats = chats.filter(chat => chat.id !== chatId);
    setChats(updatedChats);

    if (chatId === currentChatId) {
      if (updatedChats.length > 0) {
        setCurrentChatId(updatedChats[0].id);
      } else {
        const newChat = isSignedIn ? createEphemeralChat() : chatStorage.createChat();
        setChats([newChat]);
        setCurrentChatId(newChat.id);
      }
    }
  };

  const handleUpdateMessages = useCallback((chatId: string, messages: Message[], title?: string) => {
    const updates: Partial<Chat> = { messages };
    if (title) updates.title = title;
    // Signed-in: the server is already updated by the /api/chat call itself
    // (upsert_chat_turn) — this only needs to refresh local render state.
    if (!isSignedIn) chatStorage.updateChat(chatId, updates);
    setChats(prevChats =>
      prevChats.map(chat =>
        chat.id === chatId ? { ...chat, ...updates, updatedAt: Date.now() } : chat
      )
    );
  }, [isSignedIn]);

  const handleModuleChange = useCallback((chatId: string, module: string | undefined) => {
    if (!isSignedIn) chatStorage.updateChat(chatId, { selectedModule: module });
    setChats(prevChats =>
      prevChats.map(chat =>
        chat.id === chatId ? { ...chat, selectedModule: module, updatedAt: Date.now() } : chat
      )
    );
  }, [isSignedIn]);

  const handleRaiseTicketEnabled = useCallback((chatId: string) => {
    if (!isSignedIn) chatStorage.updateChat(chatId, { raiseTicket: true });
    setChats(prev => prev.map(c => c.id === chatId ? { ...c, raiseTicket: true } : c));
  }, [isSignedIn]);

  const handleNewChatWithModule = useCallback((module: string | undefined) => {
    handleNewChat(module);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats, isSignedIn]);

  const currentChat = chats.find(chat => chat.id === currentChatId);

  return (
    <div className={`App ${sidebarOpen ? 'sidebar-is-open' : ''}`}>
      <Sidebar
        chats={chats}
        currentChatId={currentChatId}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        onDeleteChat={handleDeleteChat}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onTicketRaised={(chatId, ticketId, email) =>
          setEscalatedChats(prev => ({ ...prev, [chatId]: { ticketId, email } }))
        }
        raiseTicketChatId={raiseTicketChatId}
        onClearRaiseTicketChatId={() => setRaiseTicketChatId(null)}
        loading={chatsLoading}
        canDelete={!isSignedIn}
      />

      <div className="app-main">
        <AppHeader onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main>
          {currentChat && (
            <ChatWindow
              key={currentChat.id}
              chatId={currentChat.id}
              initialMessages={currentChat.messages}
              onUpdateMessages={handleUpdateMessages}
              selectedModule={currentChat.selectedModule}
              onModuleChange={(module) => handleModuleChange(currentChat.id, module)}
              onNewChat={handleNewChatWithModule}
              chatTitle={currentChat.title}
              escalation={escalatedChats[currentChat.id]}
              raiseTicketEnabled={currentChat.raiseTicket}
              onRaiseTicketEnabled={handleRaiseTicketEnabled}
              onRaiseTicketClick={() => setRaiseTicketChatId(currentChat.id)}
            />
          )}
        </main>
      </div>
    </div>
  );
}
